/* ==========================================================================
   AYRAA — autoplay that also works in Safari
   --------------------------------------------------------------------------
   The theme's <ecx-load-video> does this on the first intersection:

       if (isIntersecting) { this.loadVideo(); this.playVideo(); }

       loadVideo()  { ... this.local_video.src = <source>'s data-src ... }
       playVideo()  { this.local_video?.play(); this.isPlaying = true; }

   Three things go wrong there, and only on Safari:

   1. play() is called in the same task as the src assignment, so readyState
      is still 0. Chrome queues the play request and honours it once data
      arrives; WebKit rejects it.

   2. sections/video-banner.liquid asks for preload="none", so nothing is
      fetched until something asks to play. Combined with (1) there is nothing
      to play at the moment play() is called.

   3. isPlaying is set to true whether or not play() succeeded -- the promise
      is dropped. playVideo() is guarded by `!this.isPlaying`, so one rejected
      play latches the flag and the video is never retried. That is the part
      that makes it permanent rather than a slow start: the element believes
      it is playing.

   This patches the prototype rather than editing assets/theme.js, which is
   minified vendor code: the two methods are replaced, the iframe branches are
   delegated back to the originals untouched, and if the theme ever renames the
   element or the methods this file does nothing at all.

   It also nudges the app-rendered videos ("Seen on you"), which are not
   <ecx-load-video> and so are not covered by the patch above. That nudge is
   deliberately narrow -- muted, inline, already marked autoplay -- so it can
   only ever start a video the page already declared should be playing.
   ========================================================================== */
(function () {
  "use strict";

  /* Safari will not autoplay unless the element is muted AND inline. Both are
     already in the markup; asserted here because a JS-set muted property that
     arrives after the load algorithm has started is ignored, and it costs
     nothing to be sure before asking to play. */
  function prepare(video) {
    video.muted = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    /* metadata, not auto: enough for WebKit to have something to start with,
       without pulling the whole file on a phone. */
    if (video.getAttribute("preload") === "none") video.preload = "metadata";
  }

  /* Ask to play, and report honestly whether it started. The caller uses that
     to decide whether to latch, so a rejection leaves the element retryable. */
  function tryPlay(video, onResult) {
    var settled = false;
    var done = function (ok) {
      if (settled) return;
      settled = true;
      onResult(ok);
    };

    var attempt = function () {
      var p;
      try {
        p = video.play();
      } catch (_) {
        done(false);
        return;
      }
      if (p && typeof p.then === "function") {
        p.then(function () { done(true); }, function () { done(false); });
      } else {
        /* Older WebKit returns undefined from play(). Fall back to reading
           back the state on the next frame. */
        requestAnimationFrame(function () { done(!video.paused); });
      }
    };

    if (video.readyState >= 2) {
      attempt();
      return;
    }

    /* Nothing buffered yet. Wait for data rather than asking now and being
       refused -- this is the case the theme got wrong. Both events are used
       because WebKit and Blink do not agree on which fires first, and `once`
       plus the settled guard keeps it to a single attempt. */
    video.addEventListener("loadeddata", attempt, { once: true });
    video.addEventListener("canplay", attempt, { once: true });

    /* preload="none" means the wait above would never end on its own. */
    if (video.networkState === 0 /* NETWORK_EMPTY */ || video.readyState === 0) {
      try { video.load(); } catch (_) {}
    }

    /* If the data never arrives, stop waiting so the element is not left
       believing a play is still pending. */
    setTimeout(function () { done(!video.paused); }, 8000);
  }

  /* ------------------------------------------------------------------ *
   * 1. <ecx-load-video> — the hero banner and anything else using it
   * ------------------------------------------------------------------ */
  function patchLoadVideo() {
    var Ctor = window.customElements && window.customElements.get("ecx-load-video");
    if (!Ctor || !Ctor.prototype || Ctor.prototype.__ayPatched) return false;

    var proto = Ctor.prototype;
    var origLoad = proto.loadVideo;
    var origPlay = proto.playVideo;
    if (typeof origLoad !== "function" || typeof origPlay !== "function") return false;

    proto.loadVideo = function () {
      if (this.dataset.videoType !== "local_video") return origLoad.apply(this, arguments);
      if (this.isLoaded) return;

      var video = this.querySelector("video");
      if (!video) { this.isLoaded = true; return; }
      this.local_video = video;

      /* The Liquid rewrites every <source src=""> to data-src so nothing
         downloads up front. The theme promoted only the first one onto
         video.src; every source is promoted here so a file with more than one
         rendition still lets the browser choose. The emptied src attribute is
         removed as it goes: a <source> with src="" is a load error in its own
         right, and WebKit is the browser that acts on it. */
      var sources = video.querySelectorAll("source[data-src]");
      var promoted = 0;
      Array.prototype.forEach.call(sources, function (s) {
        var url = s.getAttribute("data-src");
        if (!url) { s.removeAttribute("src"); return; }
        s.setAttribute("src", url);
        s.removeAttribute("data-src");
        promoted++;
      });

      /* No <source> to work with: fall back to the element-level src the
         theme used, so this is never worse than what it replaced. */
      if (!promoted && !video.getAttribute("src")) {
        var first = video.querySelector("source");
        var url = first && first.getAttribute("data-src");
        if (url) video.src = url;
      }

      prepare(video);
      this.isLoaded = true;
    };

    proto.playVideo = function () {
      if (this.dataset.videoType !== "local_video") return origPlay.apply(this, arguments);
      if (this.isPlaying || !this.isLoaded) return;

      var video = this.local_video || this.querySelector("video");
      if (!video) return;

      var self = this;
      /* Latched only on success. A refusal leaves isPlaying false, so the
         next intersection -- or the visibility handler below -- tries again
         instead of the element sitting there convinced it is playing. */
      tryPlay(video, function (ok) { self.isPlaying = !!ok; });
    };

    proto.__ayPatched = true;
    return true;
  }

  /* theme.js is deferred, so the element may not be defined yet. */
  if (!patchLoadVideo()) {
    if (window.customElements && window.customElements.whenDefined) {
      window.customElements.whenDefined("ecx-load-video").then(patchLoadVideo);
    }
    document.addEventListener("DOMContentLoaded", patchLoadVideo);
  }

  /* ------------------------------------------------------------------ *
   * 2. App-rendered videos ("Seen on you")
   *
   * Those are the shoppable-video app's own <video class="lazy">, not
   * <ecx-load-video>, so the patch above does not reach them. They already
   * carry autoplay + muted + playsinline; this only acts on ones that say so
   * and are still paused once they are on screen, so it cannot start anything
   * the page did not already ask to play, and it never touches a video a
   * person has paused themselves.
   * ------------------------------------------------------------------ */
  /* The shoppable section marks its videos data-ay-autoplay rather than
     autoplay, so the browser does not fetch all fourteen at once; a plain
     autoplay video from anywhere else is still honoured. */
  function isAutoplay(v) {
    return v.hasAttribute("autoplay") || v.hasAttribute("data-ay-autoplay");
  }

  function watchAppVideos() {
    if (!("IntersectionObserver" in window)) return;

    var seen = new WeakSet();
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (!entry.isIntersecting) {
          /* Off screen: stop streaming. Without this a slide carries on
             downloading behind the one being looked at, which is most of what
             made the rail cost 38 MB. ayIgnorePause keeps this out of the
             user-paused bookkeeping below -- the person did not pause it. */
          if (!v.paused && isAutoplay(v)) {
            v.dataset.ayIgnorePause = "1";
            try { v.pause(); } catch (_) {}
            delete v.dataset.ayIgnorePause;
          }
          return;
        }
        if (v.dataset.ayUserPaused === "1") return;
        if (!isAutoplay(v)) return;
        if (!v.paused) return;
        prepare(v);
        tryPlay(v, function () {});
      });
    }, { rootMargin: "0px 0px 100px 0px", threshold: 0.1 });

    /* An opening banner is deliberately left inert by the theme's element --
       see the comment in sections/video-banner.liquid -- so that it can
       autoplay natively with no script. It still wants pausing once it is
       scrolled past, and that is the one job left over, so it is taken in
       here. Every other <ecx-load-video> looks after itself. */
    var SKIP = 'ecx-load-video:not([data-video-type="local_video_eager"])';

    var scan = function () {
      document.querySelectorAll("video[autoplay], video[data-ay-autoplay]").forEach(function (v) {
        if (v.closest(SKIP)) return;
        if (seen.has(v)) return;
        seen.add(v);
        /* Respect a deliberate pause: once someone pauses it themselves, stop
           restarting it on every scroll past. */
        v.addEventListener("pause", function () {
          if (v.dataset.ayIgnorePause === "1") return;
          v.dataset.ayUserPaused = "1";
        });
        v.addEventListener("play", function () { delete v.dataset.ayUserPaused; });
        observer.observe(v);
      });
    };

    scan();

    /* The app injects its slides after load, and again as the rail moves.
       Batched to one scan per frame: this observes the whole document, the
       slider mutates it on every transition, and running a document-wide
       querySelectorAll synchronously inside the callback put that work in the
       middle of the very animations it was watching. */
    var queued = false;
    var rescan = function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        scan();
      });
    };

    new MutationObserver(rescan).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", watchAppVideos);
  } else {
    watchAppVideos();
  }
})();
